import React from "react"
import { Box } from "@chakra-ui/react"
import { BadgeTemplate } from "sharedTypes/badge"
import SAMPLE_GUEST_DATA from "utilities/sampleGuestData"
import { getTemplateDimensions } from "../utils/badgeHelpers"
import { PREVIEW_RESOLUTION } from "../utils/constants"
import BadgeElement from "../Elements/BadgeElement"
import { SAMPLE_EVENT_DATA } from "../utils/sampleData"
import {
  getElementTransformStyle,
  getBorderStyle,
} from "../utils/elementDisplay"
import { EventData, GuestData } from "../utils/badgeDataResolver"

type Props = {
  template: BadgeTemplate
  containerWidth?: number
  containerHeight?: number
  guestData?: GuestData
  eventData?: EventData
}

const BadgePreviewRenderer = ({
  template,
  containerWidth,
  containerHeight,
  guestData,
  eventData,
}: Props) => {
  if (!template || !template.badgeData || !template.badgeData.elements) {
    return (
      <Box
        w="full"
        h="full"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="gray.100"
        color="gray.600"
      >
        Template not available
      </Box>
    )
  }

  const { width: templateWidth, height: templateHeight } = getTemplateDimensions(template)

  let effectiveScaleFactor = PREVIEW_RESOLUTION
  if (containerWidth && containerHeight) {
    const canvasWidth = templateWidth * PREVIEW_RESOLUTION
    const canvasHeight = templateHeight * PREVIEW_RESOLUTION
    const scaleX = containerWidth / canvasWidth
    const scaleY = containerHeight / canvasHeight
    const fitScale = Math.min(scaleX, scaleY, 1)

    effectiveScaleFactor = PREVIEW_RESOLUTION * fitScale
  }

  const canvasWidth = templateWidth * effectiveScaleFactor
  const canvasHeight = templateHeight * effectiveScaleFactor

  const sortedElements = [...template.badgeData.elements].sort(
    (a, b) => a.zIndex - b.zIndex,
  )

  const previewGuestData = guestData || SAMPLE_GUEST_DATA
  const previewEventData = eventData || SAMPLE_EVENT_DATA

  return (
    <Box
      position="relative"
      width={containerWidth ? `${containerWidth}px` : "100%"}
      height={containerHeight ? `${containerHeight}px` : "100%"}
      display="flex"
      alignItems="center"
      justifyContent="center"
      overflow="hidden"
    >
      <Box
        position="relative"
        width={`${canvasWidth}px`}
        height={`${canvasHeight}px`}
        bg="white"
      >
        {sortedElements.map((element) => {
          const transformStyle = getElementTransformStyle(
            element,
            { rotation: element.transforms?.rotate || 0 },
            false,
            null,
          )

          const borderStyle = getBorderStyle(element, effectiveScaleFactor)

          return (
            <Box
              key={element.id}
              position="absolute"
              left={`${element.x * effectiveScaleFactor}px`}
              top={`${element.y * effectiveScaleFactor}px`}
              width={`${element.width * effectiveScaleFactor}px`}
              height={`${element.height * effectiveScaleFactor}px`}
              transform={transformStyle}
              transformOrigin="50% 50%"
              zIndex={element.zIndex}
              pointerEvents="none"
              backgroundColor={element.fill}
              p={0}
              borderRadius="sm"
              {...borderStyle}
            >
              <BadgeElement
                element={element}
                scaleFactor={effectiveScaleFactor}
                guestData={previewGuestData}
                eventData={previewEventData}
              />
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

export default BadgePreviewRenderer
